import React, { ReactNode } from "react";
import {
  Menu,
  MenuButton,
  IconButton,
  MenuList,
  MenuItem,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import useGetColors from "shared/hooks/useGetColors";
import { PathsE } from "shared/routes";
import Link from "./Link";
import useHandleCopy from "shared/hooks/useHandleCopy";
import { ModelFieldsT } from "shared/api/types/common";
import MuiIcon from "components/MuiIcon";
import DeleteModal from "shared/views/admin/DeleteModal";
import { useTranslation } from "react-i18next";

type Props<TData> = {
  data: TData;
  isModelView?: boolean;
  isHideEdit?: boolean;
  isHideCopyUUID?: boolean;
  children?: ReactNode;
  handleActivate?: (id: string) => void;
  handleDeactivate?: (id: string) => void;

  handleHidden?: (id: string) => void;
  isHidden?: boolean;

  handleDelete?: (id: string) => void;
  handleRestore?: (id: string) => void;
  isDeleted?: boolean;

  viewLink?: PathsE;
  editLink?: PathsE;
  // TODO add more specific type
  pathsParams?: Record<string, string>;
};

type CommonModelT = ModelFieldsT;
// type CommonModelT = ModelFieldsT & DeletedModelT;

const MainItemMenu = <TData extends UnknownRecord & CommonModelT>({
  children,
  data,
  handleActivate,
  handleDeactivate,
  handleDelete,
  handleRestore,
  isDeleted,
  handleHidden,
  isHidden,
  viewLink,
  pathsParams,
  editLink,
  isHideCopyUUID,
  isModelView = false,
}: Props<TData>) => {
  const { gray100, gray500, gray700 } = useGetColors();
  const handleCopy = useHandleCopy();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { t } = useTranslation();

  return (
    <Menu>
      <MenuButton
        my={isModelView ? "" : "-10px"}
        as={IconButton}
        aria-label="Options"
        icon={<MuiIcon name="more_vert" size={24} color={gray500} />}
        variant={isModelView ? "outline" : ""}
        borderRadius="8px"
      />
      <MenuList>
        {!!editLink && (
          <Link path={editLink} pathsParams={pathsParams}>
            <MenuItem
              _hover={{
                backgroundColor: gray100,
              }}
            >
              <MuiIcon name="edit" />
              <Text color={gray700} ml="8px" fontSize="sm">
                Edit
              </Text>
            </MenuItem>
          </Link>
        )}
        {!!viewLink && (
          <Link path={viewLink} pathsParams={pathsParams}>
            <MenuItem
              _hover={{
                backgroundColor: gray100,
              }}
            >
              <MuiIcon name="folder_open" />
              <Text color={gray700} ml="8px" fontSize="sm">
                View
              </Text>
            </MenuItem>
          </Link>
        )}

        {!!handleActivate &&
          (data.isActive ? (
            <MenuItem
              _hover={{
                backgroundColor: gray100,
              }}
              onClick={() => handleDeactivate(data.id)}
            >
              <MuiIcon name="do_not_disturb_on" />
              <Text color={gray700} ml="8px" fontSize="sm">
                Deactivate
              </Text>
            </MenuItem>
          ) : (
            <MenuItem
              _hover={{
                backgroundColor: gray100,
              }}
              onClick={() => handleActivate(data.id)}
            >
              <MuiIcon name="add_circle" />
              <Text color={gray700} ml="8px" fontSize="sm">
                Activate
              </Text>
            </MenuItem>
          ))}
        {!isHideCopyUUID && (
          <MenuItem
            _hover={{
              backgroundColor: gray100,
            }}
            onClick={() => handleCopy(data.id)}
          >
            <MuiIcon name="content_copy" />
            <Text color={gray700} ml="8px" fontSize="sm">
              Copy UUID
            </Text>
          </MenuItem>
        )}
        {handleHidden && (
          <MenuItem
            _hover={{
              backgroundColor: gray100,
            }}
            onClick={() => handleHidden(data.id)}
          >
            <MuiIcon name={isHidden === true ? "visibility" : "visibility_off"} />
            <Text color={gray700} ml="8px" fontSize="sm">
              {isHidden === true ? "Show" : "Hide"}
            </Text>
          </MenuItem>
        )}
        {handleDelete && !isDeleted && (
          <>
            <MenuItem _hover={{ backgroundColor: gray100}} onClick={onOpen}>
              <MuiIcon name="delete" />
              <Text color={gray700} ml="8px" fontSize="sm">
                {t("buttons.delete")}
              </Text>
            </MenuItem>
            <DeleteModal
              title={t("Are you sure?")}
              text={""}
              onClose={onClose}
              isOpen={isOpen}
              onSubmit={() => handleDelete(data.id)}
            />
          </>
        )}
        {handleRestore && isDeleted && (
          <MenuItem _hover={{ backgroundColor: gray100}} onClick={() => handleRestore(data.id)}>
            <MuiIcon name="restore" />
            <Text color={gray700} ml="8px" fontSize="sm">
              {t("buttons.restore")}
            </Text>
          </MenuItem>
        )}
        {children}
      </MenuList>
    </Menu>
  );
};

export default MainItemMenu;
